/* eslint-disable max-len */

import * as React from "react";
import { Button, Divider, Grid, Tab, Segment, Loader } from "semantic-ui-react";

import { Detail } from "@neworbit/simpleui-detail";
import { useSelector } from "react-redux";

import Utils from "@teamprevent/common/utils";
import { userRegistrationStatusText } from "@teamprevent/common/models/core";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { CoreDetailProps } from "../Detail";
import { ApplicationState } from "../../../../applicationState";
import { useEmployeeRecordDetailsModel } from "../../hooks";

import { CoreDetailsButtons } from "./CoreDetailButtons";
import { CloseMonitoringSection } from "./CloseMonitoringSection";

import { Link } from "redux-little-router";

export const CoreDetails: React.FunctionComponent<CoreDetailProps> = (props) => {
    const eppCloseMonitoring = getFeatureToggles()[FeatureToggleNames.EPPCloseMonitoring];

    return (<Grid>
        {eppCloseMonitoring ?
            <Grid.Row>
                <Grid.Column width={8}>
                    <Grid padded={false}>
                        <Detail label="Personal Reference Number" value={props.employeeRecord.personalReferenceNumber} labelWidth={10} valueWidth={6} />
                        <Detail label="Employee Unique ID" value={props.employeeRecord.secondaryReference} labelWidth={10} valueWidth={6} />
                        <Detail label="Legacy ID" value={props.employeeRecord.legacyPersonalId} labelWidth={10} valueWidth={6} />
                    </Grid>
                </Grid.Column>
                <Grid.Column width={8}>
                    <CloseMonitoringSection customerId={props.employeeRecord.customerId} employeeRecord={props.employeeRecord} />
                </Grid.Column>
            </Grid.Row> :
            <>
                <Detail label="Personal Reference Number" value={props.employeeRecord.personalReferenceNumber} labelWidth={5} valueWidth={11} />
                <Detail label="Employee Unique ID" value={props.employeeRecord.secondaryReference} labelWidth={5} valueWidth={11} />
                <Detail label="Legacy ID" value={props.employeeRecord.legacyPersonalId} labelWidth={5} valueWidth={11} />
            </>}
        <Detail label="Title" value={props.employeeRecord.title} labelWidth={5} valueWidth={11} />
        <Detail label="First Name" value={props.employeeRecord.firstName} labelWidth={5} valueWidth={11} />
        <Detail label="Last Name" value={props.employeeRecord.lastName} labelWidth={5} valueWidth={11} />
        <Detail label="Previous Last Name" value={props.employeeRecord.previousLastName} labelWidth={5} valueWidth={11} />
        <Detail label="Gender" value={props.employeeRecord.gender} labelWidth={5} valueWidth={11} />
        <Detail label="Organisation Structure One" value={props.employeeRecord.orgStructureOne && props.employeeRecord.orgStructureOne.name} labelWidth={5} valueWidth={11} />
        <Detail label="Organisation Structure Two" value={props.employeeRecord.orgStructureTwo && props.employeeRecord.orgStructureTwo.name} labelWidth={5} valueWidth={11} />
        <Detail label="Organisation Structure Three" value={props.employeeRecord.orgStructureThree && props.employeeRecord.orgStructureThree.name} labelWidth={5} valueWidth={11} />
        <Detail label="Organisation Structure Four" value={props.employeeRecord.orgStructureFour && props.employeeRecord.orgStructureFour.name} labelWidth={5} valueWidth={11} />
        <Detail label="Work Groups" value={props.employeeRecord.workGroups && props.employeeRecord.workGroups.map(workGroup => workGroup.name).join(", ")} labelWidth={5} valueWidth={11} />
        <Detail label="Date Of Birth" value={props.employeeRecord.dateOfBirth && props.employeeRecord.dateOfBirth.format("L")} labelWidth={5} valueWidth={11} />
        <Detail
            label="Age"
            value={props.employeeRecord.dateOfBirth && Utils.calculateAge(props.employeeRecord.dateOfBirth.format("L"))}
            labelWidth={5}
            valueWidth={11}
        />
        <Detail label="Personal Email Address" value={props.employeeRecord.personalEmailAddress} labelWidth={5} valueWidth={11} />
        <Detail label="Work Email Address" value={props.employeeRecord.workEmailAddress} labelWidth={5} valueWidth={11} />
        <Detail label="Home Phone Number" value={props.employeeRecord.homePhoneNumber} labelWidth={5} valueWidth={11} />
        <Detail label="Work Phone Number" value={props.employeeRecord.workPhoneNumber} labelWidth={5} valueWidth={11} />
        <Detail label="Mobile Phone Number" value={props.employeeRecord.mobilePhoneNumber} labelWidth={5} valueWidth={11} />
        <Detail label="Address Line 1" value={props.employeeRecord.addressLine1} labelWidth={5} valueWidth={11} />
        <Detail label="Address Line 2" value={props.employeeRecord.addressLine2} labelWidth={5} valueWidth={11} />
        <Detail label="Address Line 3" value={props.employeeRecord.addressLine3} labelWidth={5} valueWidth={11} />
        <Detail label="City" value={props.employeeRecord.city} labelWidth={5} valueWidth={11} />
        <Detail label="County" value={props.employeeRecord.county} labelWidth={5} valueWidth={11} />
        <Detail label="Post Code" value={props.employeeRecord.postCode} labelWidth={5} valueWidth={11} />
        <Detail label="Job Title" value={props.employeeRecord.jobTitle} labelWidth={5} valueWidth={11} />
        <Detail label="Critical Notes" value={props.employeeRecord.criticalNotes} labelWidth={5} valueWidth={11} />
        <Detail label="Termination Date" value={props.employeeRecord.terminationDate && props.employeeRecord.terminationDate.format("L")} labelWidth={5} valueWidth={11} />
        <Detail label="Customer Id" value={props.employeeRecord.customerId} labelWidth={5} valueWidth={11} />
        <Detail label="Customer Name" value={props.employeeRecord.customerName} labelWidth={5} valueWidth={11} />
        <Detail
            label="Active"
            value={props.employeeRecord.active !== null && props.employeeRecord.active !== undefined
                && props.employeeRecord.active.toString()}
            labelWidth={5}
            valueWidth={11}
        />
        <Detail label="Registration Status" value={userRegistrationStatusText[props.employeeRecord.registrationStatus]} labelWidth={5} valueWidth={11} />
        <Detail label="Registered Email Address" value={props.employeeRecord.registeredEmailAddress} labelWidth={5} valueWidth={11} />
    </Grid>);
};

const CoreDetailsWithActions: React.FC = () => {
    const path = useSelector((state: ApplicationState) => state.router.pathname!);
    const employeeRecordId = useSelector((state: ApplicationState) => state.router.params!.employeeRecordId);
    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const { employeeRecord, isLoaded: isEmployeeLoaded } = useEmployeeRecordDetailsModel({
        employeeRecordId,
        customerId
    });
    if (isEmployeeLoaded === false) {
        return <Loader active />;
    }

    return (
        <div>
            <CoreDetails employeeRecord={employeeRecord} />
            <Divider />
            <Segment basic clearing vertical>
                <CoreDetailsButtons employeeRecord={employeeRecord} />
                <Button icon="pencil" content="Edit" floated="right" as={Link} href={`${path}/edit`} />
            </Segment>
        </div>
    );
};

export const coreDetailsTab = {
    path: "",
    menuItem: "Details",
    render: () => <Tab.Pane><CoreDetailsWithActions /></Tab.Pane>
};
